/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import Link from "next/link";
import { Dispatch, PropsWithChildren, SetStateAction, useEffect } from "react";
import { isFunction } from "lodash-es";
import { Box } from "@otrium/core";
import { LinkProps } from "next/dist/client/link";
import {
  BrandGateLevelInCustomerPlatform,
  CatalogProduct,
  HomepageSingleCategoryProduct,
  ProductLabel,
  WishlistItemType,
} from "src/types/graphql.d";
import { ProductLabels } from "src/molecules/ProductLabels";
import { ProductPhoto } from "src/molecules/ProductPhoto";
import { formatName } from "src/utils/formatName";
import {
  StyledCardBrand,
  StyledCardPrices,
  StyledCardProduct,
  StyledCardRegularPrice,
  StyledCardSizes,
  StyledProductCard,
  Image,
  LastTileVideo,
  LoginButton,
} from "./ProductCard.styled";
import { Price } from "src/atoms/Price";
import { BREAKPOINTS } from "src/breakpoints";
import {
  blurredLookbookImageLoader,
  lookbookImageLoader,
} from "src/utils/lookbook-image-loader";
import {
  GATED_PRODUCT_CARD_WIDTHS,
  PRODUCT_CARD_WIDTHS,
} from "src/utils/image-widths";
import { ToggleWishListButton } from "src/molecules/ToggleWishListButton";
import { useInView } from "react-intersection-observer";
import { SEGMENT_IN_VIEW_THRESHOLD } from "src/segment";
import { SegmentProductList } from "src/hooks/useSegmentProductList";
import { ProductOrderCartButton } from "src/molecules/ProductOrderCartButton";
import { useTheme, Theme } from "@emotion/react";
import { useLingui } from "@lingui/react";
import { t, Trans } from "@lingui/macro";
import { savePdpResultId } from "src/utils/listing-persistence";
import { getRecommendationItemAttributes } from "src/utils/constructor-beacon";
import { LockIcon } from "@otrium/icons";
import classNames from "classnames";
import { Skeleton } from "src/atoms/Skeleton";

type ProductCartProps = {
  centerText?: boolean;
  className?: string;
  getImageElementWrapper?: Dispatch<SetStateAction<HTMLDivElement | null>>;
  hideIconsLabel?: boolean;
  isAddedToWishList: boolean | undefined;
  onAddToWishList: (itemType: WishlistItemType, itemId: string) => void;
  onRemoveWishList: (itemType: WishlistItemType, itemId: string) => void;
  position: number;
  product: CatalogProduct | HomepageSingleCategoryProduct;
  labels?: ProductLabel[];
  segmentProductOnClick?: SegmentProductList["segmentProductOnClick"];
  setVisibleProducts?: SegmentProductList["setVisibleProducts"];
  onProductClickTracker?: (itemId: string) => void;
  showLabels?: boolean;
  showName?: boolean;
  showSizes?: boolean;
  timedToggleWishlist?: boolean;
  trackingDisabled?: boolean;
  wishlistProgress: boolean;
  zoomImageOnHover?: boolean;
  isOutOfStock?: boolean;
  allowAddToCart?: boolean;
  addToCartShortLabel?: boolean;
  onAddToCart?: (productSlug: string) => void;
  onGateProductClick?: () => void;
  isLastTile?: boolean;
  shopType?: string;
  onSearchProductClick?: () => void;
  cnstrcRecommendation?: boolean;
  /** When set, emits data-cnstrc-item-section on the card root. Caller decides
   * the section name to match what Constructor's index expects
   * (e.g. "Products" for autocomplete/PLP/recommendations). */
  cnstrcSection?: string;
  /** Strategy the pod resolved to; attached to the beacon only when the backend sent one. */
  cnstrcStrategyId?: string;
  showHoverBorder?: boolean;
  /** Constructor result_id of the listing this card belongs to. When set, it is
   * persisted to sessionStorage on click so PDP tracking events can attribute
   * the search/browse result that led to the product (PLP → PDP handoff). */
  resultId?: string;
  /** Rendered width of the tile per breakpoint. Defaults to the listing grid;
   * carousels render narrower slides and pass their own. */
  imageSizes?: string;
  isGatedUser: boolean;
  isGateResolved: boolean;
};

// See docs/tech/responsive-images.md for how these were derived.

const PRODUCT_CARD_SIZES = `(max-width: ${BREAKPOINTS.sm}) 50vw, (max-width: ${BREAKPOINTS.lg}) 33vw, 420px`;

// The gated carousels' "Log in" tile loops a short video served straight from
// public/, bypassing /_next/image, which cannot resize animated images.
export const LAST_TILE_MEDIA = {
  poster: "/static/images/carousel-last-card.jpg",
  webm: "/static/images/carousel-last-card.webm",
  mp4: "/static/images/carousel-last-card.mp4",
};

type LinkWrapperProps = PropsWithChildren<LinkProps> & {
  outOfStock: boolean;
  isGated: boolean;
};

const LinkWrapper = ({
  outOfStock,
  children,
  isGated,
  ...linkProps
}: LinkWrapperProps) => {
  return outOfStock || isGated ? (
    <>{children}</>
  ) : (
    <Link {...linkProps} prefetch={false}>
      {children}
    </Link>
  );
};

function ProductCard({
  centerText = false,
  className,
  getImageElementWrapper,
  hideIconsLabel,
  isAddedToWishList,
  onAddToWishList,
  onRemoveWishList,
  onProductClickTracker,
  position,
  product,
  setVisibleProducts,
  showLabels = true,
  showName = true,
  showSizes = true,
  wishlistProgress,
  zoomImageOnHover,
  isOutOfStock = false,
  allowAddToCart = false,
  addToCartShortLabel = false,
  onAddToCart,
  onGateProductClick,
  isLastTile = false,
  shopType,
  onSearchProductClick,
  cnstrcRecommendation = false,
  cnstrcSection,
  cnstrcStrategyId,
  showHoverBorder = false,
  resultId,
  imageSizes = PRODUCT_CARD_SIZES,
  isGatedUser,
  isGateResolved,
}: ProductCartProps): JSX.Element {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: SEGMENT_IN_VIEW_THRESHOLD,
  });
  const theme: Theme = useTheme();
  const { i18n } = useLingui();

  const gatedLabel = {
    type: "gated",
    value: t(i18n)`Log in to access`,
    color: theme.colors.tone.white,
    priority: 1,
    text_color: theme.colors.tone.white,
    background_color: theme.colors.tone.black,
  };

  useEffect(() => {
    if (inView && isFunction(setVisibleProducts)) {
      setVisibleProducts(product, position);
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  const gateLevel =
    product?.gate?.level || (product as CatalogProduct)?.brand?.gate?.level;

  const isPartiallyGated =
    isGatedUser &&
    gateLevel === BrandGateLevelInCustomerPlatform.PartiallyGated;

  const productLabels = showLabels ? product?.labels || [] : [];

  const isFullygated =
    isGatedUser && gateLevel === BrandGateLevelInCustomerPlatform.FullyGated;

  const isGated = isPartiallyGated || isFullygated;

  const allLabels = isGated ? [gatedLabel] : productLabels.slice(0, 2);

  const handleProductClick = () => {
    if (isGated && onGateProductClick) {
      onGateProductClick();
      return;
    }

    if (resultId && !isOutOfStock) {
      savePdpResultId(product.slug, resultId);
    }

    if (onSearchProductClick) {
      onSearchProductClick();
    }

    if (onProductClickTracker) {
      onProductClickTracker(product.id);
    }
  };

  if (!product) {
    return <></>;
  }

  const cnstrcItemPrice =
    "price_float" in product ? product.price_float : product.price;

  return (
    <StyledProductCard
      showHoverBorder={showHoverBorder}
      centerText={centerText}
      className={className}
      data-testid="product-card"
      onClick={handleProductClick}
      ref={ref} // For segment tracking
      isGated={isGated}
      isLastTile={isLastTile}
      data-cnstrc-item-id={product.id}
      data-cnstrc-item-name={product.name}
      data-cnstrc-item-price={cnstrcItemPrice}
      {...(cnstrcSection && { "data-cnstrc-item-section": cnstrcSection })}
      {...getRecommendationItemAttributes({
        isRecommendation: cnstrcRecommendation,
        strategyId: cnstrcStrategyId,
      })}
    >
      {isGated || isLastTile ? (
        <Box
          data-testid="product-card-lock-icon"
          sx={{
            position: "absolute",
            right: "8px",
            top: "8px",
            zIndex: 1,
            display: "block",
          }}
        >
          <LockIcon
            color={
              isLastTile ? theme.colors.tone.white : theme.colors.tone.black
            }
          ></LockIcon>
        </Box>
      ) : (
        <Box data-wishlist-hover={isAddedToWishList}>
          <ToggleWishListButton
            isAddedToWishList={isAddedToWishList}
            itemType={WishlistItemType.Product}
            itemId={product.id}
            loading={wishlistProgress}
            onAddToWishList={onAddToWishList}
            onRemoveWishList={onRemoveWishList}
          />
        </Box>
      )}
      <LinkWrapper
        outOfStock={isOutOfStock}
        href={{ pathname: "/product/[productSlug]" }}
        as={`/product/${product.slug}`}
        passHref
        isGated={isGated || isLastTile}
      >
        <Box
          display="block"
          sx={{
            position: "relative",
            cursor: "pointer",
          }}
          ref={getImageElementWrapper}
        >
          <ProductPhoto
            className="ProductView__productPhotos"
            data-expand="-20"
            zoomImageOnHover={zoomImageOnHover}
            data-loaded="false"
          >
            {!isGateResolved ? (
              <Skeleton width="100%" height="100%" />
            ) : isLastTile ? (
              <LastTileVideo
                data-testid="last-card-video"
                poster={LAST_TILE_MEDIA.poster}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden
                onLoadedData={(event) =>
                  event.currentTarget.parentElement?.setAttribute(
                    "data-loaded",
                    "true"
                  )
                }
              >
                <source src={LAST_TILE_MEDIA.webm} type="video/webm" />
                <source src={LAST_TILE_MEDIA.mp4} type="video/mp4" />
              </LastTileVideo>
            ) : (
              <Image
                loader={
                  isGated ? blurredLookbookImageLoader : lookbookImageLoader
                }
                className={classNames({
                  ["is-gated"]: isGated,
                })}
                mobile={{ src: product.thumbnail }}
                alt={`${product.name} by ${product.brand.name}`}
                sizes={imageSizes}
                widths={
                  isGated ? GATED_PRODUCT_CARD_WIDTHS : PRODUCT_CARD_WIDTHS
                }
                withContainer={false}
                onLoad={(event) =>
                  event.currentTarget?.parentElement?.setAttribute(
                    "data-loaded",
                    "true"
                  )
                }
              />
            )}
          </ProductPhoto>
          {isLastTile && (
            <LoginButton
              data-testid="last-card-login-button"
              className="ProductView__loginButton"
              variant="primary"
              colorScheme="light"
              onClick={onGateProductClick}
            >
              {product.name}
            </LoginButton>
          )}
          {allLabels.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "8px",
                bottom: "8px",
                display: "block",
              }}
            >
              <ProductLabels
                labels={allLabels}
                hideIconsLabel={hideIconsLabel}
                isGated={isGated}
              />
            </Box>
          )}
        </Box>
        <StyledCardBrand>{formatName(product?.brand?.name)}</StyledCardBrand>
        {showName && !isFullygated && (
          <StyledCardProduct>{formatName(product.name)}</StyledCardProduct>
        )}
        {isFullygated && (
          <StyledCardProduct>
            <Trans>Sign in to view</Trans>
          </StyledCardProduct>
        )}
        {!isLastTile && !isFullygated && (
          <>
            <StyledCardPrices>
              <Price
                price={
                  // NOTE: 'price' is 'number' in CatalogProduct but 'string' in HomepageSingleCategoryProduct,
                  // so we have to use 'price_float' from there
                  "price_float" in product ? product.price_float : product.price
                }
              />
              {("regular_price_float" in product || product.regular_price) && (
                <StyledCardRegularPrice>
                  <Price
                    // NOTE: 'regular_price' is 'number' in CatalogProduct but 'string' in HomepageSingleCategoryProduct,
                    // so we have to use 'regular_price_float' from there
                    price={
                      "regular_price_float" in product
                        ? product.regular_price_float
                        : product.regular_price
                    }
                  />
                  {"*"}
                </StyledCardRegularPrice>
              )}
            </StyledCardPrices>
            {showSizes &&
              "available_sizes_label" in product && ( // NOTE: HomepageSingleCategoryProduct has no 'available_sizes_label'
                <StyledCardSizes>
                  {product?.available_sizes_label}
                </StyledCardSizes>
              )}
          </>
        )}
      </LinkWrapper>
      {allowAddToCart && onAddToCart && (
        <Box py={3}>
          <ProductOrderCartButton
            variant="tertiary"
            isOutOfStock={isOutOfStock}
            shortLabel={addToCartShortLabel}
            onClick={() => onAddToCart(product.slug)}
            data-testid="product-add-to-cart-cta"
            fullWidth
          />
        </Box>
      )}
    </StyledProductCard>
  );
}

export default ProductCard;
